import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from 'components/shadcn/ui/alert';
import { Button } from 'components/shadcn/ui/button';
import { acknowledgeUpgradeRecord } from '../UpgradeHistory/service';
import { useDevice } from '../service';
import { useJwtToken } from 'helper';
import { useGetIDsFromURL } from 'components/DeviceDetails/helper';
import { useToast } from 'components/shadcn/hooks/use-toast';
import { LoadingSpinner } from 'components/shadcn/ui/loadingSpinner';

const statusMessages = {
    Initiated: 'Upgrade initiated...',
    Idle: 'No upgrade in progress',
    PendingDownload: 'Upgrade requested, waiting to download firmware from AWS',
    Downloading: 'Firmware is being downloaded by the device',
    DownloadComplete:
        'Firmware downloaded and verified, waiting for installation',
    Installing: 'Firmware being written to the device',
    Complete: 'Upgrade successful, awaiting user acknowledgment',
    Failed: 'Upgrade failed, check `UpgradeLog` for details',
};

const failedStatuses = {
    Failed: 'Failed',
    Unknown: 'Failed',
};

const severityVariant = {
    error: 'error',
    success: 'success',
    info: 'secondary',
};

export const UpgradeProgress = ({ status }) => {
    const accessToken = useJwtToken();
    const { Thing_ID } = useGetIDsFromURL();
    const { data: device } = useDevice(Thing_ID);
    const { toast } = useToast();
    const mappedStatus = failedStatuses[status] || status;
    const message = statusMessages[mappedStatus] || statusMessages.Failed;
    const [showLoader, setShowLoader] = useState(false);
    const [showMessage, setShowMessage] = useState(true);

    const severity =
        mappedStatus === 'Failed'
            ? 'error'
            : mappedStatus === 'Complete'
              ? 'success'
              : 'info';
    const showButton = mappedStatus === 'Failed' || mappedStatus === 'Complete';

    const handleAcknowledge = async () => {
        setShowLoader(true);
        const request = {
            Thing_ID: device?.Thing_ID,
            UpdateID: device?.Config?.data?.UpdateID,
        };
        try {
            const acknowledgedResponse = await acknowledgeUpgradeRecord(
                request,
                accessToken
            );
            if (acknowledgedResponse?.upgradedRecord?.acknowledged) {
                setShowMessage(false);
            } else {
                throw new Error(acknowledgedResponse);
            }
        } catch (error) {
            console.error('Error acknowledging upgrade record:', error);
            toast({
                title: 'Upgrade Progress Acknowledgement',
                description: 'Error acknowledging upgrade record.',
                variant: 'destructive',
            });
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        if (mappedStatus !== 'Complete' && mappedStatus !== 'Failed') {
            setShowLoader(true);
        }
    }, [mappedStatus]);

    if (!status || !showMessage) return null;

    return (
        <Alert
            variant={severityVariant[severity]}
            className="flex max-h-16 w-max items-center gap-3"
        >
            <AlertDescription className="flex items-center gap-2">
                {message}
                {showLoader && (
                    <LoadingSpinner className="ml-2 h-6 w-6 opacity-40" />
                )}
            </AlertDescription>
            {showButton && !showLoader && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAcknowledge}
                    className="h-[40px] w-full"
                >
                    Acknowledge
                </Button>
            )}
        </Alert>
    );
};

export default UpgradeProgress;
